/**
 * Phaser-free triangle-mesh builder for static terrain (docs/PERFORMANCE.md).
 *
 * Emits the exact geometry Phaser's Graphics renderer would generate per frame for
 * `fillPoints`, `fillTriangle`, `fillEllipse`, `strokePoints`, and `lineBetween` — Earcut
 * fills, two-triangle line quads with the bevel join quad Phaser adds when the stroke is
 * wider than 2 px, and the loop join on a closed path — but once, into typed arrays the
 * game layer uploads to a `STATIC_DRAW` buffer.
 *
 * Vertex layout matches `BatchHandlerTriFlat`: `x`, `y` as float32 and one packed
 * `AARRGGBB` tint as four normalized bytes (12-byte stride). Indices are 16-bit, so a
 * builder splits into chunks before a chunk would pass 65 535 vertices.
 */

import type { Vec2 } from '../types.ts';

/** Earcut signature: flat `[x0, y0, x1, y1, …]` → triangle indices. */
export type Triangulate = (flat: number[]) => number[];

export type MeshChunk = {
  /** Interleaved `x, y, tint` per vertex; view as Float32Array and Uint32Array. */
  vertexData: ArrayBuffer;
  vertexCount: number;
  indices: Uint16Array;
};

export const MESH_VERTEX_STRIDE_BYTES = 12;
export const MESH_MAX_VERTICES = 65_535;
/** Phaser adds a bevel quad between segments once the stroke is wider than this. */
const STROKE_JOIN_MIN_WIDTH = 2;
const ELLIPSE_SMOOTHNESS = 32;

/** Same packing as Phaser's `Utils.getTintAppendFloatAlpha`. */
export function packTint(rgb: number, alpha: number): number {
  const a = ((Math.max(0, Math.min(1, alpha)) * 255) | 0) & 0xff;

  return ((a << 24) | (rgb & 0xffffff)) >>> 0;
}

export function meshTriangleCount(chunks: readonly MeshChunk[]): number {
  let total = 0;

  for (const chunk of chunks) {
    total += chunk.indices.length / 3;
  }

  return total;
}

export class TriMeshBuilder {
  private readonly triangulate: Triangulate;
  private readonly chunks: MeshChunk[] = [];
  private positions: number[] = [];
  private tints: number[] = [];
  private indices: number[] = [];
  private vertexCount = 0;

  constructor(triangulate: Triangulate) {
    this.triangulate = triangulate;
  }

  get isEmpty(): boolean {
    return this.chunks.length === 0 && this.indices.length === 0;
  }

  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    tint: number,
  ): void {
    this.reserve(3);
    const base = this.vertexCount;
    this.pushVertex(x0, y0, tint);
    this.pushVertex(x1, y1, tint);
    this.pushVertex(x2, y2, tint);
    this.indices.push(base, base + 1, base + 2);
  }

  /** Two triangles sharing the diagonal; `a b c d` in ring order. */
  fillQuad(a: Vec2, b: Vec2, c: Vec2, d: Vec2, tint: number): void {
    this.reserve(4);
    const base = this.vertexCount;
    this.pushVertex(a.x, a.y, tint);
    this.pushVertex(b.x, b.y, tint);
    this.pushVertex(c.x, c.y, tint);
    this.pushVertex(d.x, d.y, tint);
    this.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  /** Earcut fill of a simple polygon. Consecutive duplicate points are dropped like Phaser does. */
  fillPolygon(points: readonly Vec2[], tint: number): void {
    const flat = dedupeFlat(points);
    const count = flat.length / 2;

    if (count < 3) {
      return;
    }

    const triangles = this.triangulate(flat);

    if (triangles.length < 3) {
      return;
    }

    this.reserve(count);
    const base = this.vertexCount;

    for (let i = 0; i < flat.length; i += 2) {
      this.pushVertex(flat[i]!, flat[i + 1]!, tint);
    }

    for (const index of triangles) {
      this.indices.push(base + index);
    }
  }

  /** Convex fan — no Earcut needed. Mirrors `Graphics.fillEllipse` at smoothness 32. */
  fillEllipse(
    cx: number,
    cy: number,
    width: number,
    height: number,
    tint: number,
    smoothness = ELLIPSE_SMOOTHNESS,
  ): void {
    if (width <= 0 || height <= 0 || smoothness < 3) {
      return;
    }

    this.reserve(smoothness);
    const base = this.vertexCount;
    const rx = width / 2;
    const ry = height / 2;

    for (let i = 0; i < smoothness; i += 1) {
      const angle = (i / smoothness) * Math.PI * 2;
      this.pushVertex(cx + rx * Math.cos(angle), cy + ry * Math.sin(angle), tint);
    }

    for (let i = 1; i < smoothness - 1; i += 1) {
      this.indices.push(base, base + i, base + i + 1);
    }
  }

  lineBetween(x0: number, y0: number, x1: number, y1: number, width: number, tint: number): void {
    this.strokePolyline([{ x: x0, y: y0 }, { x: x1, y: y1 }], width, false, tint);
  }

  /**
   * Phaser `StrokePath`: one quad per segment (TL, BL, BR, TR), a bevel quad joining
   * consecutive segments when `width > 2`, and on a closed path the closing segment plus
   * the join back to the first quad.
   */
  strokePolyline(points: readonly Vec2[], width: number, closed: boolean, tint: number): void {
    const path = dedupePoints(points);

    if (closed && path.length > 1) {
      const first = path[0]!;
      const last = path[path.length - 1]!;

      if (first.x !== last.x || first.y !== last.y) {
        path.push(first);
      }
    }

    const segmentCount = path.length - 1;

    if (segmentCount < 1 || width <= 0) {
      return;
    }

    this.reserve(segmentCount * 4);
    const half = width / 2;
    const connect = width > STROKE_JOIN_MIN_WIDTH && segmentCount > 1;
    const firstBase = this.vertexCount;
    let previousBase = -1;

    for (let i = 0; i < segmentCount; i += 1) {
      const a = path[i]!;
      const b = path[i + 1]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy);

      if (length <= 0) {
        continue;
      }

      // Perpendicular offset, same orientation as Phaser's DrawLine.
      const px = (half * dy) / length;
      const py = (half * -dx) / length;
      const base = this.vertexCount;
      this.pushVertex(a.x + px, a.y + py, tint); // TL
      this.pushVertex(a.x - px, a.y - py, tint); // BL
      this.pushVertex(b.x - px, b.y - py, tint); // BR
      this.pushVertex(b.x + px, b.y + py, tint); // TR
      this.indices.push(base, base + 1, base + 2, base + 2, base + 3, base);

      if (connect && previousBase >= 0) {
        // TL, BL, previous BR, previous TR
        this.indices.push(
          base,
          base + 1,
          previousBase + 2,
          previousBase + 2,
          previousBase + 3,
          base,
        );
      }

      previousBase = base;
    }

    if (connect && closed && previousBase >= 0 && previousBase !== firstBase) {
      // BR, TR, first TL, first BL
      this.indices.push(
        previousBase + 2,
        previousBase + 3,
        firstBase,
        firstBase,
        firstBase + 1,
        previousBase + 2,
      );
    }
  }

  /** Close the current chunk and return every chunk built so far. */
  finish(): MeshChunk[] {
    this.flushChunk();
    return this.chunks.slice();
  }

  private reserve(vertices: number): void {
    if (this.vertexCount + vertices > MESH_MAX_VERTICES) {
      this.flushChunk();
    }
  }

  private pushVertex(x: number, y: number, tint: number): void {
    this.positions.push(x, y);
    this.tints.push(tint);
    this.vertexCount += 1;
  }

  private flushChunk(): void {
    if (this.vertexCount === 0 || this.indices.length === 0) {
      this.positions = [];
      this.tints = [];
      this.indices = [];
      this.vertexCount = 0;
      return;
    }

    const vertexData = new ArrayBuffer(this.vertexCount * MESH_VERTEX_STRIDE_BYTES);
    const floats = new Float32Array(vertexData);
    const words = new Uint32Array(vertexData);

    for (let i = 0; i < this.vertexCount; i += 1) {
      floats[i * 3] = this.positions[i * 2]!;
      floats[i * 3 + 1] = this.positions[i * 2 + 1]!;
      words[i * 3 + 2] = this.tints[i]!;
    }

    this.chunks.push({
      vertexData,
      vertexCount: this.vertexCount,
      indices: Uint16Array.from(this.indices),
    });

    this.positions = [];
    this.tints = [];
    this.indices = [];
    this.vertexCount = 0;
  }
}

function dedupePoints(points: readonly Vec2[]): Vec2[] {
  const out: Vec2[] = [];

  for (const point of points) {
    const last = out[out.length - 1];

    if (last && last.x === point.x && last.y === point.y) {
      continue;
    }

    out.push(point);
  }

  return out;
}

function dedupeFlat(points: readonly Vec2[]): number[] {
  const flat: number[] = [];
  let lastX = Number.NaN;
  let lastY = Number.NaN;

  for (const point of points) {
    if (point.x === lastX && point.y === lastY) {
      continue;
    }

    flat.push(point.x, point.y);
    lastX = point.x;
    lastY = point.y;
  }

  // A closing duplicate of the first point is dropped as well; Earcut handles the loop.
  if (flat.length >= 4 && flat[0] === lastX && flat[1] === lastY) {
    flat.length -= 2;
  }

  return flat;
}
